<template>
  <b-modal
    id="modal-participant-cancel"
    ref="modalParticipantCancel"
    title="Cancel Participants"
    size="xl"
    hide-footer
    @hidden="resetModal">
    <div v-if="selectedBatch">
      <!-- Batch Info Header -->
      <div class="mb-4">
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <h5 class="mb-2">{{ selectedCourse?.title }} - {{ formatBatchLabel(selectedBatch) }}</h5>
            <div class="d-flex gap-2 align-items-center">
              <span class="badge" :class="getBatchStatusClass(selectedBatch.batch_status)">
                {{ selectedBatch.batch_status }}
              </span>
              <span class="text-muted">•</span>
              <small class="text-muted">
                Quota: {{ selectedBatch.quota }} | Enrolled: {{ selectedBatch.enrolled }}
              </small>
            </div>
          </div>
          <div>
            <span class="badge bg-primary">
              {{ selectedParticipants.length }} Selected
            </span>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="mt-3 text-muted mb-0">Loading participants...</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="participants.length === 0" class="text-center py-5">
        <i class="fe fe-users" style="font-size: 3rem; color: #dee2e6;"></i>
        <p class="mt-3 text-muted mb-0">No participants found</p>
      </div>

      <!-- Participants Table -->
      <div v-else class="table-responsive">
        <table class="table table-sm table-hover">
          <thead>
            <tr>
              <th style="width: 40px;">
                <div class="custom-control custom-checkbox">
                  <input 
                    type="checkbox" 
                    class="custom-control-input" 
                    id="selectAll"
                    :checked="isAllSelected"
                    @change="toggleSelectAll">
                  <label class="custom-control-label" for="selectAll"></label>
                </div>
              </th>
              <th style="width: 40px;">No</th>
              <th>NIK</th>
              <th>Full Name</th>
              <th>Position</th>
              <th>Company</th>
              <th>Status</th>
              <th style="width: 250px;">Reason</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(participant, index) in participants" :key="participant.enrollment_id">
              <td>
                <div class="custom-control custom-checkbox">
                  <input 
                    type="checkbox" 
                    class="custom-control-input" 
                    :id="`participant-${participant.enrollment_id}`"
                    :value="participant.enrollment_id"
                    v-model="selectedParticipants"
                    :disabled="!canSubmitUnenrollment(participant)">
                  <label class="custom-control-label" :for="`participant-${participant.enrollment_id}`"></label>
                </div>
              </td>
              <td>{{ index + 1 }}</td>
              <td>
                <span class="badge badge-soft-primary">{{ participant.nik }}</span>
              </td>
              <td>{{ participant.fullname }}</td>
              <td>{{ participant.position_level }}</td>
              <td>
                <small class="text-muted">{{ participant.company }}</small>
              </td>
              <td>
                <span class="badge" :class="getParticipantStatusClass(participant.status)">
                  {{ getParticipantStatusLabel(participant.status) }}
                </span>
              </td>
              <td>
                <textarea 
                  v-model="participantReasons[participant.enrollment_id]"
                  class="form-control form-control-sm"
                  rows="2"
                  placeholder="Enter reason for cancellation..."
                  :disabled="!selectedParticipants.includes(participant.enrollment_id)"
                ></textarea>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Action Buttons -->
      <div v-if="hasPendingParticipants" class="mt-4 d-flex gap-2 justify-content-end">
        <button 
          type="button" 
          class="btn btn-secondary"
          @click="closeModal">
          Close
        </button>
        <button 
          type="button" 
          class="btn btn-danger"
          :disabled="selectedParticipants.length === 0 || isCancelling"
          @click="cancelParticipants">
          <span v-if="isCancelling">
            <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Cancelling...
          </span>
          <span v-else>
            <i class="fe fe-x me-2"></i>
            Ajukan Unenrollment ({{ selectedParticipants.length }})
          </span>
        </button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex';
import { formatBatchLabel } from '@/utils/batchIdentity';

export default {
  name: 'ModalParticipantCancel',
  props: {
    selectedCourse: {
      type: Object,
      default: null
    },
    selectedBatch: {
      type: Object,
      default: null
    },
    batchIndex: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      selectedParticipants: [],
      participantReasons: {},
      loading: false,
      isCancelling: false
    };
  },
  watch: {
    selectedBatch: {
      immediate: true,
      handler(newBatch) {
        if (newBatch && newBatch.company_participants) {
          // Only approved enrollments can be submitted. Pending requests remain
          // visible for status tracking, but cannot be submitted twice.
          this.selectedParticipants = newBatch.company_participants
            .filter(this.canSubmitUnenrollment)
            .map(p => p.enrollment_id);
        }
      }
    }
  },
  computed: {
    participants() {
      return this.selectedBatch?.company_participants || [];
    },
    
    hasPendingParticipants() {
      return this.participants.some(this.canSubmitUnenrollment);
    },
    
    isAllSelected() {
      const eligibleParticipants = this.participants.filter(this.canSubmitUnenrollment);
      return eligibleParticipants.length > 0 &&
             this.selectedParticipants.length === eligibleParticipants.length;
    }
  },
  methods: {
    ...mapActions('mitraUnenrollment', {
      cancelParticipantsAction: 'cancelParticipants'
    }),
    formatBatchLabel,
    
    resetModal() {
      this.selectedParticipants = [];
      this.participantReasons = {};
    },
    
    closeModal() {
      this.$refs.modalParticipantCancel.hide();
    },
    
    toggleSelectAll(event) {
      if (event.target.checked) {
        this.selectedParticipants = this.participants
          .filter(this.canSubmitUnenrollment)
          .map(p => p.enrollment_id);
      } else {
        // Deselect all
        this.selectedParticipants = [];
      }
    },
    
    async cancelParticipants() {
      const result = await this.$swal({
        title: 'Batalkan Pendaftaran Peserta?',
        html: `You are about to Unenrollment for <strong>${this.selectedParticipants.length}</strong> participant(s).<br/>This action cannot be undone.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Ya, batalkan pendaftaran',
        confirmButtonColor: '#e63757',
        cancelButtonText: 'Tidak, biarkan',
        reverseButtons: true
      });
      
      if (result.isConfirmed) {
        this.isCancelling = true;
        try {
          // Prepare data with reasons
          const cancelledData = this.selectedParticipants.map(enrollmentId => ({
            enrollment_id: enrollmentId,
            reason: this.participantReasons[enrollmentId] || ''
          }));

          await this.cancelParticipantsAction({
            batchId: this.selectedBatch.training_event_id,
            data: { cancelled: cancelledData }
          });

          this.$swal({
            title: 'Success',
            text: `${this.selectedParticipants.length} pengajuan unenrollment berhasil dikirim ke HSE Training.`,
            icon: 'success',
            confirmButtonText: 'OK',
            confirmButtonColor: '#e63757'
          });

          // Reset selections and close modal
          this.selectedParticipants = [];
          this.participantReasons = {};
          this.closeModal();
          
          // Refresh batch data and courses
          this.$emit('refresh-batch');
          this.$emit('refresh-courses');
          this.$emit('close-batch-modal');
        } catch (error) {
          // Extract error message from response
          const errorMessage = error.response?.data?.error || 
                               error.response?.data?.message || 
                               error.message || 
                               'Peserta belum berhasil dibatalkan';
          
          this.$swal({
            title: 'Error',
            text: errorMessage,
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#e63757'
          });
        } finally {
          this.isCancelling = false;
        }
      }
    },
    
    getBatchStatusClass(status) {
      const classes = {
        'upcoming': 'badge-soft-warning',
        'ongoing': 'badge-soft-success',
        'completed': 'badge-soft-info',
        'cancelled': 'badge-soft-danger'
      };
      return classes[status?.toLowerCase()] || 'badge-soft-secondary';
    },

    canSubmitUnenrollment(participant) {
      return String(participant?.status || '').trim().toLowerCase() === 'approved';
    },

    getParticipantStatusLabel(status) {
      const normalizedStatus = String(status || '').trim().toLowerCase();
      const labels = {
        approved: 'Enrolled',
        'waiting for cancellation approval': 'Menunggu Approval HSE Training',
        'waiting for hse training unenrollment approval': 'Menunggu Approval HSE Training',
        cancelled: 'Unenrollment Disetujui',
        rejected: 'Unenrollment Ditolak'
      };
      return labels[normalizedStatus] || status || '-';
    },

    getParticipantStatusClass(status) {
      const normalizedStatus = String(status || '').trim().toLowerCase();
      const classes = {
        approved: 'badge-soft-success',
        'waiting for cancellation approval': 'badge-soft-warning',
        'waiting for hse training unenrollment approval': 'badge-soft-warning',
        cancelled: 'badge-soft-danger',
        rejected: 'badge-soft-danger'
      };
      return classes[normalizedStatus] || 'badge-soft-secondary';
    }
  }
};
</script>

<style scoped>
.me-2 {
  margin-right: 0.5rem;
}

.gap-2 {
  gap: 0.5rem;
}

.badge-soft-primary {
  color: #2c7be5;
  background-color: rgba(44, 123, 229, 0.1);
}

.badge-soft-warning {
  color: #f6c343;
  background-color: rgba(246, 195, 67, 0.1);
}

.badge-soft-success {
  color: #00c9a7;
  background-color: rgba(0, 201, 167, 0.1);
}

.badge-soft-info {
  color: #39afd1;
  background-color: rgba(57, 175, 209, 0.1);
}

.badge-soft-danger {
  color: #e63757;
  background-color: rgba(230, 55, 87, 0.1);
}

.badge-soft-secondary {
  color: #6c757d;
  background-color: rgba(108, 117, 125, 0.1);
}

.bg-primary {
  background-color: #2c7be5 !important;
  color: white;
}
</style>
